import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */

export var akEditorFullPageDefaultFontSize = 16;
export var akEditorCodeFontFamily = "var(--ds-font-family-code, ui-monospace, Menlo, \"Segoe UI Mono\", \"Ubuntu Mono\", monospace)";
export var akEditorSubtleAccent = "var(--ds-background-accent-gray-subtler, #DCDFE4)";
export var akEditorBlockquoteBorderColor = "var(--ds-border, #091E4224)";
export var akEditorCodeBackground = "var(--ds-background-accent-gray-subtlest, #F1F2F4)";
export var akEditorCodeBlockPadding = '12px';
export var akEditorCodeInlinePadding = '2px 4px';
export var akEditorDeleteBackground = "var(--ds-background-danger, #FFECEB)";
export var akEditorDeleteBackgroundWithOpacity = 'rgba(255, 189, 173, 0.5)'; // R75 with 50% opacity
export var akEditorDeleteBorder = "var(--ds-border-danger, #E2483D)";
export var akEditorDeleteIconColor = "var(--ds-icon-danger, #C9372C)";
export var akEditorCustomIconSize = 20;
export var akEditorSelectedBorderColor = "var(--ds-border-selected, #0C66E4)";
export var akEditorSelectedBorderSize = 1;
export var akEditorSelectedBorderBoldSize = 2;
export var akEditorSelectedBorder = "".concat(akEditorSelectedBorderSize, "px solid ", "var(--ds-border-selected, #0C66E4)");
export var akEditorSelectedBoxShadow = "0 0 0 ".concat(akEditorSelectedBorderSize, "px ", "var(--ds-border-selected, #0C66E4)");
export var akEditorSelectedBoldBoxShadow = "0 0 0 ".concat(akEditorSelectedBorderBoldSize, "px ", "var(--ds-border-selected, #0C66E4)");
export var akEditorSelectedBlanketOpacity = 0.3;
export var akEditorUnitZIndex = 1;
export var akEditorShadowZIndex = 2;
export var akEditorStickyHeaderZIndex = 11; // filmstrip uses 10 for its shadow
export var akEditorSmallZIndex = akEditorStickyHeaderZIndex + 1;
export var akEditorGridLineZIndex = 2;

// z-index for main menu bar -
// this is highest as it should be above anything else in editor below.
export var akEditorMenuZIndex = 500;
// z-index used for floating toolbars like code block, table etc
export var akEditorFloatingPanelZIndex = 400;
// z-index used for pickers (date, emoji, mentions) and type-aheads, hyperlinks
export var akEditorFloatingDialogZIndex = akEditorMenuZIndex + 10;
// z-index used for table cell menu options button on a sticky header
export var akEditorTableCellOnStickyHeaderZIndex = akEditorFloatingDialogZIndex - 5;
// z-index used for floating toolbars table cell menu which are above block toolbars
export var akEditorFloatingOverlapPanelZIndex = akEditorFloatingPanelZIndex + 5;
export var akEditorTableToolbarSize = 11;
export var akEditorTableBorder = "var(--ds-background-accent-gray-subtler, #DCDFE4)";
export var akEditorTableToolbar = "var(--ds-background-accent-gray-subtlest, #F1F2F4)";
export var akEditorTableHeaderCellBackground = '#F1F2F4';
export var akEditorTableHeaderCellBackgroundDark = '#2C333A';
export var akEditorTableBorderSelected = "var(--ds-border-focused, #388BFF)";
export var akEditorTableBorderRadius = '3px';
export var akEditorTableCellBackgroundOpacity = 0.5;
export var akEditorFullPageMaxWidth = 680;
export var akEditorDefaultLayoutWidth = 760;
export var akEditorWideLayoutWidth = 960; // This value is used for tables with breakouts and is used to calculate `breakoutWidthPx`
export var akEditorFullWidthLayoutWidth = 1800;
export var akEditorFullWidthLayoutLineLength = 1792;
export var akEditorTableNumberColumnWidth = 42;
export var akEditorBreakoutPadding = 96;
export var akEditorGutterPadding = 32;
export var akEditorGutterPaddingDynamic = function akEditorGutterPaddingDynamic() {
  return fg('platform_editor_core_increase_full_page_guttering') ? 52 : 32;
};
export var akEditorMobileBreakoutPoint = 720;
export var akEditorTableCellMinWidth = 48;
export var akEditorTableLegacyCellMinWidth = 128;
export var akEditorMediaResizeHandlerPaddingWide = 12;
export var akEditorMediaResizeHandlerPadding = 4;
export var akEditorSwoopCubicBezier = "cubic-bezier(0.15, 1, 0.3, 1)";
export var gridMediumMaxWidth = 1024;
export var breakoutWideScaleRatio = 1.33;
export var akEditorCalculatedWideLayoutWidthSmallViewport = 905; // from breakoutConsts.calcWideWidth, layoutMaxWidth * breakoutConsts.wideScaleRatio = 904.8 ~ 905 This is a resulting width value that is applied to nodes that currently use breakouts (except table) and are set to `wide` when the viewport's width is <= 1266px.
export var akEditorCalculatedWideLayoutWidth = 1011; // = akEditorDefaultLayoutWidth * breakoutWideScaleRatio = 1010.8 ~ 1011 This is a resulting width value that is applied to nodes that currently use breakouts (except table) and are set to `wide` when the viewport's width is > 1329px.
export var akRichMediaResizeZIndex = akEditorUnitZIndex * 99;
export var akLayoutGutterOffset = 12;
export var akEditorLineHeight = 1.714;
export var akEditorRuleBorderRadius = '1px';
export var akEditorToolbarKeylineHeight = 2;
export var akEditorContextPanelWidth = 320;
export var akEditorTableCellBlanketSelected = 'rgba(179, 212, 255, 0.3)';
export var akEditorTableCellBlanketDeleted = 'rgba(255, 235, 230, 0.3)';
export var akEditorWrappedNodeZIndex = akEditorUnitZIndex * 2; //prevent paragraph nodes from rendering above wrapped nodes

export var layoutBreakpointWidth = {
  SMALL: 550,
  MEDIUM: 760
};

// @see typography spreadsheet: https://docs.google.com/spreadsheets/d/1iYusRGCT4PoPfvxbJ8NrgjtfFgXLm5lpDWXzjua1W2E/edit#gid=93913128
export var blockNodesVerticalMargin = '0.75rem';
export var DEFAULT_EMBED_CARD_WIDTH = 680;
export var DEFAULT_EMBED_CARD_HEIGHT = 480;
export var MAX_BROWSER_SCROLLBAR_HEIGHT = 20;

// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required -- Ignored via go/ED-25883
// @deprecated
export var ATLASSIAN_NAVIGATION_HEIGHT = '56px';
var DEFAULT_FONT_SIZE = 14;
export var FULL_PAGE_EDITOR_TOOLBAR_HEIGHT = function FULL_PAGE_EDITOR_TOOLBAR_HEIGHT() {
  if (editorExperiment('platform_editor_controls', 'variant1', {
    exposure: true
  })) {
    return "var(--ds-space-500, 40px)";
  }
  if (fg('live_pages_content_jump_mitigation')) {
    return '2.188rem';
  }
  return "var(--ds-space-500, 40px)";
};
export var akEditorSelectedNodeClassName = 'ak-editor-selected-node';
export var editorFontSize = function editorFontSize(_ref) {
  var theme = _ref.theme;
  return theme && theme.baseFontSize ? theme.baseFontSize : DEFAULT_FONT_SIZE;
};
export var relativeSize = function relativeSize(multiplier) {
  return function (_ref2) {
    var theme = _ref2.theme;
    return editorFontSize({
      theme: theme
    }) * multiplier;
  };
};
export var relativeFontSizeToBase16 = function relativeFontSizeToBase16(px) {
  if (typeof px === 'string') {
    px = parseInt(px);
  }
  if (isNaN(px)) {
    throw new Error("Invalid font size: '".concat(px, "'"));
  }
  return "".concat(px / 16, "rem");
};
export var VIEWPORT_SIZES = {
  laptopHiDPI: {
    width: 1440,
    height: 900
  },
  laptopMDPI: {
    width: 1280,
    height: 800
  },
  tabletL: {
    width: 1024,
    height: 1366
  },
  tabletS: {
    width: 768,
    height: 1024
  },
  mobileM: {
    width: 414,
    height: 736
  },
  mobileS: {
    width: 375,
    height: 667
  },
  mobileXS: {
    width: 320,
    height: 568
  }
};

// to be updated in ED-10790: this should be variable threshold based on how many buttons enabled on main toolbar
export var akEditorMobileMaxWidth = 0;
export var getTableCellBackgroundDarkModeColors = [['White', '#000000'], ['Light blue', '#0C294F'], ['Light teal', '#0C343B'], ['Light green', '#052E21'], ['Light yellow', '#484123'], ['Light red', '#441C13'], ['Light purple', '#282249'], ['Light gray', '#202328'], ['Blue', '#0B3165'], ['Teal', '#044853'], ['Green', '#053927'], ['Yellow', '#6F5C25'], ['Red', '#582013'], ['Purple', '#3E327B'], ['Gray', '#475262'], ['Dark blue', '#003884'], ['Dark teal', '#055866'], ['Dark green', '#044932'], ['Dark yellow', '#82641C'], ['Dark red', '#6B2A19'], ['Dark purple', '#4D38B2']].map(function (_ref3) {
  var _ref4 = _slicedToArray(_ref3, 2),
    colorName = _ref4[0],
    colorValue = _ref4[1];
  return getTableCellBackgroundDarkModeColorCSS(colorName, colorValue);
}).join('\n');
export function getTableCellBackgroundDarkModeColorCSS(colorName, colorValue) {
  return "\n  &[colorname=\"".concat(colorName, "\"] {\n    background-color: ").concat(colorValue, " !important; // !important to override default style color\n  }\n  ");
}
export var avatarColors = ["var(--ds-icon-accent-red, #C9372C)", "var(--ds-background-accent-red-bolder-hovered, #AE2E24)", "var(--ds-icon-accent-magenta, #CD519D)", "var(--ds-background-accent-magenta-bolder-hovered, #943D73)", "var(--ds-icon-accent-orange, #E56910)", "var(--ds-background-accent-orange-bolder-hovered, #A54800)", "var(--ds-icon-accent-green, #22A06B)", "var(--ds-background-accent-green-bolder-hovered, #216E4E)", "var(--ds-icon-accent-blue, #1D7AFC)", "var(--ds-background-accent-blue-bolder-hovered, #0055CC)", "var(--ds-icon-accent-teal, #2898BD)", "var(--ds-background-accent-teal-bolder-hovered, #206A83)", "var(--ds-icon-accent-lime, #6A9A23)", "var(--ds-background-accent-lime-bolder-hovered, #4C6B1F)", "var(--ds-icon-accent-purple, #8270DB)", "var(--ds-background-accent-purple-bolder-hovered, #5E4DB2)", "var(--ds-icon-accent-gray, #758195)", "var(--ds-background-accent-gray-bolder-hovered, #44546F)", "var(--ds-icon-accent-yellow, #B38600)", "var(--ds-background-accent-yellow-bolder-hovered, #7F5F01)", "var(--ds-background-accent-yellow-bolder-pressed, #533F04)"];

// MDS-569: Will update references to avatarColors to use participantColors instead, we will then deprecate avatarColors
export var participantColors = [{
  textColor: "var(--ds-text-inverse, #FFFFFF)",
  backgroundColor: "var(--ds-background-accent-red-bolder, #C9372C)"
}, {
  textColor: "var(--ds-text-inverse, #FFFFFF)",
  backgroundColor: "var(--ds-background-accent-blue-bolder, #0C66E4)"
}, {
  textColor: "var(--ds-text-inverse, #FFFFFF)",
  backgroundColor: "var(--ds-background-accent-green-bolder, #1F845A)"
}, {
  textColor: "var(--ds-text-inverse, #FFFFFF)",
  backgroundColor: "var(--ds-background-accent-yellow-bolder, #946F00)"
}, {
  textColor: "var(--ds-text-inverse, #FFFFFF)",
  backgroundColor: "var(--ds-background-accent-purple-bolder, #6E5DC6)"
}, {
  textColor: "var(--ds-text-inverse, #FFFFFF)",
  backgroundColor: "var(--ds-background-accent-magenta-bolder, #AE4787)"
}, {
  textColor: "var(--ds-text-inverse, #FFFFFF)",
  backgroundColor: "var(--ds-background-accent-teal-bolder, #227D9B)"
}, {
  textColor: "var(--ds-text-inverse, #FFFFFF)",
  backgroundColor: "var(--ds-background-accent-orange-bolder, #C25100)"
}, {
  textColor: "var(--ds-text-inverse, #FFFFFF)",
  backgroundColor: "var(--ds-background-accent-lime-bolder, #5B7F24)"
}, {
  textColor: "var(--ds-text-inverse, #FFFFFF)",
  backgroundColor: "var(--ds-background-accent-gray-bolder, #626F86)"
}, {
  textColor: "var(--ds-text-accent-gray-bolder, #091E42)",
  backgroundColor: "var(--ds-background-accent-blue-subtle, #579DFF)"
}, {
  textColor: "var(--ds-text-accent-gray-bolder, #091E42)",
  backgroundColor: "var(--ds-background-accent-red-subtle, #F87168)"
}, {
  textColor: "var(--ds-text-accent-gray-bolder, #091E42)",
  backgroundColor: "var(--ds-background-accent-orange-subtle, #FEA362)"
}, {
  textColor: "var(--ds-text-accent-gray-bolder, #091E42)",
  backgroundColor: "var(--ds-background-accent-yellow-subtle, #F5CD47)"
}, {
  textColor: "var(--ds-text-accent-gray-bolder, #091E42)",
  backgroundColor: "var(--ds-background-accent-green-subtle, #4BCE97)"
}, {
  textColor: "var(--ds-text-accent-gray-bolder, #091E42)",
  backgroundColor: "var(--ds-background-accent-teal-subtle, #6CC3E0)"
}, {
  textColor: "var(--ds-text-accent-gray-bolder, #091E42)",
  backgroundColor: "var(--ds-background-accent-purple-subtle, #9F8FEF)"
}, {
  textColor: "var(--ds-text-accent-gray-bolder, #091E42)",
  backgroundColor: "var(--ds-background-accent-magenta-subtle, #E774BB)"
}];